import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';

const DEBUG_VERBOSE = false;
export const HHDSDialogTagName: string = 'hhds-dialog';
const TAG_NAME: string = HHDSDialogTagName;
const LOG_PREFIX = `[${HHDSDialogTagName}]`;

const TRANSITION_DURATION_START: number = 0.2;
const TRANSITION_DURATION_END: number = 0.5;

export enum HHDSDialogEvent {
	show = 'show',
	hide = 'hide',
	hideComplete = 'hideComplete',
	next = 'next',
	prev = 'prev',
}

export const HHDSDialogAttrNames = {
	showNav: 'show-nav',
};

const Attrs = HHDSDialogAttrNames;

export class HHDSDialog extends Component {
	constructor() {
		super();
	}

	override init(): void {
		const closeIcon: string = `<hhds-icon type='circlecross'></hhds-icon>`;
		const prevIcon: string = `<hhds-icon type='chevronleft'></hhds-icon>`;
		const nextIcon: string = `<hhds-icon type='chevronright'></hhds-icon>`;
		const showNav: boolean = this.vars.get<boolean>(Attrs.showNav);

		const navHtml: string = `
			<hhds-button class="${TAG_NAME}__prev-button" type="modal-nav">${prevIcon}</hhds-button>
			<hhds-button class="${TAG_NAME}__next-button" type="modal-nav">${nextIcon}</hhds-button>
		`;

		const html = `
    <div class="${HHDSDialogTagName}">
      <dialog>
				<div class="dialog-inner">
					<div class="dialog-content">
						<div class="slot-container"><slot name="content"></slot></div>
						<hhds-button class="${TAG_NAME}__close-button" type="modal-close">${closeIcon}</hhds-button>
						${showNav ? navHtml : ''}
					</div>
				</div>
      </dialog>
    </div>`;
		this.setInnerHtml('', '', html);

		const closeButton = this.element(`.${TAG_NAME}__close-button`);
		const dialog = this.element<HTMLDialogElement>('dialog');

		closeButton?.addEventListener('click', () => this.beginHide());

		const prevButton = this.element(`.${TAG_NAME}__prev-button`);
		const nextButton = this.element(`.${TAG_NAME}__next-button`);

		dialog.addEventListener('click', (event) => {
			const dialogContent = this.element('.dialog-content');
			const rect = dialogContent.getBoundingClientRect();

			let isInDialog =
				event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
			isInDialog ||= event.currentTarget === dialogContent;

			if (!isInDialog) {
				this.beginHide();
			}
		});

		prevButton?.addEventListener('click', (event: Event) => {
			event.preventDefault();
			event.stopPropagation();
			this.emitEvent(HHDSDialogEvent.prev);
		});
		nextButton?.addEventListener('click', (event: Event) => {
			event.preventDefault();
			event.stopPropagation();
			this.emitEvent(HHDSDialogEvent.next);
		});
	}

	setContent(innerHtml: string): void {
		const slotContainer = this.element('.slot-container');
		if (slotContainer) {
			slotContainer.innerHTML = innerHtml;
		}
	}

	removeInstantly(): void {
		document.documentElement.classList.toggle('no-scroll', false);
		const dialog = this.root.querySelector('dialog') as HTMLDialogElement;
		if (dialog) {
			dialog.remove();
		}
	}

	private beginHide(): void {
		this.element(`.${HHDSDialogTagName}`).style.setProperty('--transition-duration', `${TRANSITION_DURATION_END}s`);
		const dialog = this.root.querySelector('dialog') as HTMLDialogElement;
		dialog.close();
		this.emitEvent(HHDSDialogEvent.hide);
		this.emitDocumentEvent(HHDSDialogEvent.hide);
		window.setTimeout(() => {
			this.emitEvent(HHDSDialogEvent.hideComplete);
			this.emitDocumentEvent(HHDSDialogEvent.hideComplete);
			document.documentElement.classList.toggle('no-scroll', false);
		}, TRANSITION_DURATION_END * 1000);
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
		//this.observeSlotChanges(false);
	}

	show(dispatchEvent: boolean): void {
		this.element(`.${HHDSDialogTagName}`).style.setProperty('--transition-duration', `${TRANSITION_DURATION_START}s`);
		document.documentElement.classList.toggle('no-scroll', true);
		const dialog = this.root.querySelector('dialog') as HTMLDialogElement;
		dialog.showModal();

		dialog.addEventListener('cancel', () => {
			this.beginHide();
		});

		if (dispatchEvent) {
			this.emitEvent(HHDSDialogEvent.show);
			this.emitDocumentEvent(HHDSDialogEvent.show);
		}
	}

	hide(): void {
		this.beginHide();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: Element[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	override onAttributeChanged(name: string, _oldValue: string, newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'Attribute changed: ', name, _oldValue, newValue);
		this.reinit();
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}

	static create(innerHtml: string | null, parent?: HTMLElement, showNav: boolean = false): HHDSDialog {
		const dialogHtml: string = `<${HHDSDialogTagName} ${HHDSDialogAttrNames.showNav}="${showNav ? 'true' : 'false'}">${innerHtml ?? ''}</${HHDSDialogTagName}>`;
		if (parent) {
			// Add directly into a parent to avoid a repeat connection to the DOM
			parent.innerHTML = dialogHtml;
			return parent.querySelector(HHDSDialogTagName) as HHDSDialog;
		} else {
			const dialogWrapper = document.createElement('div');
			dialogWrapper.innerHTML = dialogHtml;
			return dialogWrapper.firstChild as HHDSDialog;
		}
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.showNav]: {
		description: 'Show navigation buttons',
		type: Boolean,
		defaultValue: false,
	},
};
